import { ThemeCompany } from '@toggle/toggle';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorBoundaryWrapper } from '~/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { useNavigateToAssetOverview } from '~/hooks/use-navigate-to-asset-overview/useNavigateToAssetOverview';
import { useNavigateToThemeOverview } from '~/hooks/use-navigate-to-theme-overview/useNavigateToThemeOverview';
import { useQueryParams } from '~/hooks/use-query-params/useQueryParams';
import { queryKeys } from '~/routes/query-params';
import { DrawerSectionHeader } from '~/views/theme-overview/theme-overview-drawer/components/drawer-section-header/DrawerSectionHeader';
import { THEME_TAB_COMPANIES } from '~/views/theme-overview/theme-overview-header/ThemeOverviewHeader';

import { CompanyItem } from './company-item/CompanyItem';
import * as S from './TopCompaniesSection.styles';

interface TopCompaniesSectionProps {
  companies: ThemeCompany[];
  remainingCount: number;
}

export const TopCompaniesSection = ({
  companies,
  remainingCount,
}: TopCompaniesSectionProps) => {
  const { t } = useTranslation(['widget']);
  const { params } = useQueryParams();
  const { navigateToPage } = useNavigateToAssetOverview();
  const { navigateToTheme } = useNavigateToThemeOverview();

  const openAssetOverview = (value: string) => {
    navigateToPage({ entityTag: value });
  };

  const openThemeOverview = () => {
    const themeId = params[queryKeys.themeId];
    const newSearchParams = new URLSearchParams();
    newSearchParams.set(queryKeys.activeSubTab, THEME_TAB_COMPANIES);
    navigateToTheme({
      themeId: themeId || '',
      searchParams: newSearchParams,
    });
  };

  return (
    <ErrorBoundaryWrapper>
      <S.Root>
        <S.Wrapper>
          <DrawerSectionHeader
            title={t('themeOverview.topCompaniesTitle', {
              total: companies.length,
            })}
            description={t('themeOverview.rankedBySignificance')}
          />
          <ErrorBoundaryWrapper>
            <>
              <S.GridContainer>
                {companies.map(company => (
                  <S.CompanyItemWrapper key={company.company}>
                    <CompanyItem
                      company={company}
                      onPillClick={() => openAssetOverview(company.company)}
                      iconSize={20}
                    />
                  </S.CompanyItemWrapper>
                ))}
              </S.GridContainer>
              {!!remainingCount && (
                <S.StyledButtonLink
                  label={t('widget:themeOverview.viewMoreCompanies', {
                    total: remainingCount,
                  })}
                  iconName="ChevronLightRight"
                  iconPosition="right"
                  onClick={() => openThemeOverview()}
                />
              )}
            </>
          </ErrorBoundaryWrapper>
        </S.Wrapper>
      </S.Root>
    </ErrorBoundaryWrapper>
  );
};
